import { isDelegableContext, usesGatewayPathKey, getContextDescriptor, resolveContextBaseUrl, getActiveApp, DELEGATED_PATH_SEGMENT } from '@quickbee/api-config';
export { UnreachableContextError } from '@quickbee/api-config';
import { sharedRefreshCoordinator } from '@quickbee/auth/internal';
import { computeBackoffDelay, generateCorrelationId } from '@quickbee/utils';
import { useAuthStore } from '@quickbee/auth';

// src/factory/createApiClient.ts

// src/download/fileDownload.ts
function parseContentDispositionFileName(header) {
  if (!header) return null;
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended?.[2]) {
    try {
      return decodeURIComponent(extended[2].trim());
    } catch {
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  return bare?.[1]?.trim() || null;
}
function fileNameFromUrl(url) {
  const [path] = url.split("?");
  const segments = (path ?? "").split("/").filter(Boolean);
  const last = segments[segments.length - 1];
  return last ? decodeURIComponent(last) : "download";
}
async function toFileDownload(response, url) {
  const blob = await response.blob();
  return {
    blob,
    fileName: parseContentDispositionFileName(response.headers.get("content-disposition")) ?? fileNameFromUrl(url),
    contentType: response.headers.get("content-type") ?? blob.type ?? "application/octet-stream"
  };
}
function saveFileDownload(download) {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("[api-client] saveFileDownload requires a browser document.");
  }
  const objectUrl = URL.createObjectURL(download.blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = download.fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

// src/envelope/unwrapEnvelope.ts
function isSuccessEnvelope(body) {
  return typeof body === "object" && body !== null && "data" in body;
}
function isPagedEnvelope(body) {
  if (typeof body !== "object" || body === null) return false;
  const candidate = body;
  return Array.isArray(candidate.data) && typeof candidate.pagination === "object" && candidate.pagination !== null;
}
function isErrorEnvelope(body) {
  return typeof body === "object" && body !== null && "errors" in body && Array.isArray(body.errors);
}
function isPagedResult(value) {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value;
  return Array.isArray(candidate.items) && typeof candidate.pagination === "object" && candidate.pagination !== null;
}
function normalisePagination(pagination) {
  const total = pagination?.total;
  return {
    cursor: pagination?.cursor ?? null,
    hasMore: pagination?.hasMore ?? false,
    total: total === void 0 || total === null || total < 0 ? null : total
  };
}
function unwrapEnvelope(body) {
  if (isPagedEnvelope(body)) {
    return {
      items: body.data,
      pagination: normalisePagination(body.pagination)
    };
  }
  if (isSuccessEnvelope(body)) {
    return body.data;
  }
  return body;
}
function readResponseMeta(body) {
  if (typeof body !== "object" || body === null) return void 0;
  const meta = body.meta;
  if (typeof meta !== "object" || meta === null) return void 0;
  return meta;
}

// src/errors/errorCodeMap.ts
var errorCodeMap = {
  // ─── Validation / Bad Request (400) ────────────────────────────────────
  VALIDATION_400_001: { category: "validation", retryable: false },
  VALIDATION_400_002: { category: "validation", retryable: false },
  VALIDATION_400_003: { category: "validation", retryable: false },
  MISSING_TENANT_400_001: { category: "validation", retryable: false },
  IDEMPOTENCY_KEY_MISSING_400_001: { category: "validation", retryable: false },
  // The caller is branch-scoped and named no branch. `validation`, not `authorization`: they may
  // well be entitled to do this, they have not said which branch they are doing it from, and an
  // "you lack permission" toast would send an operator hunting a grant they already hold.
  MISSING_BRANCH_400_002: { category: "validation", retryable: false },
  // ─── Authentication (401) ──────────────────────────────────────────────
  AUTH_REQUIRED_401_001: { category: "auth", retryable: false },
  AUTH_TOKEN_INVALID_401_002: { category: "auth", retryable: false },
  SESSION_INVALIDATED_401_003: { category: "auth", retryable: false },
  // ─── Authorization (403) ───────────────────────────────────────────────
  AUTHZ_FORBIDDEN_403_001: { category: "authorization", retryable: false },
  TENANT_ACCESS_DENIED_403_001: { category: "authorization", retryable: false },
  // The caller's role grants do not reach the branch they named. Distinct from
  // AUTHZ_FORBIDDEN: they hold the permission, their grant is pinned elsewhere, and the fix is a
  // wider grant or a different branch rather than a different role.
  BRANCH_ACCESS_DENIED_403_003: { category: "authorization", retryable: false },
  IDEMPOTENT_REPLAY_FORBIDDEN_403_004: { category: "authorization", retryable: false },
  // The tenant does not hold the application at all — no role would help; the fix is a feature
  // toggle or a plan change. Still `authorization` for the UI: the screen is not theirs.
  APPLICATION_NOT_ENTITLED_403_002: { category: "authorization", retryable: false },
  // ─── Not Found (404) ────────────────────────────────────────────────────
  NOT_FOUND_404_001: { category: "not_found", retryable: false },
  // ─── Conflict (409) ─────────────────────────────────────────────────────
  OPTIMISTIC_LOCK_CONFLICT_409_001: { category: "conflict", retryable: false },
  SESSION_CONFLICT_409_002: { category: "conflict", retryable: false },
  IDEMPOTENT_REQUEST_IN_PROGRESS_409_003: { category: "conflict", retryable: true },
  // Not retryable: re-sending the same body under the same key gets the same refusal. The caller
  // either meant the original request (re-send its body) or a different one (mint a fresh key).
  IDEMPOTENCY_KEY_BODY_MISMATCH_422_003: { category: "conflict", retryable: false },
  // ─── Unprocessable Entity (422) — generic ─────────────────────────────
  BUSINESS_RULE_VIOLATION_422_001: { category: "business", retryable: false },
  RESOURCE_EXHAUSTED_422_002: { category: "business", retryable: false },
  // ─── QuickBee Finance-Specific (422) ────────────────────────────────────
  GOLD_BALANCE_INSUFFICIENT: { category: "business", retryable: false },
  KYC_PENDING: { category: "business", retryable: false },
  SCHEME_LOCKED: { category: "business", retryable: false },
  CERTIFICATE_EXPIRED: { category: "business", retryable: false },
  PRICE_STALE: { category: "business", retryable: false },
  AML_FLAGGED: { category: "business", retryable: false },
  // ─── Rate Limiting (429) ───────────────────────────────────────────────
  RATE_LIMIT_EXCEEDED_429_001: { category: "rate_limit", retryable: true },
  // ─── Message Processing ────────────────────────────────────────────────
  MESSAGE_PROCESSING_FAILED: { category: "server", retryable: false },
  MESSAGE_PROCESSING_TRANSIENT_FAILURE: { category: "server", retryable: true },
  // ─── Internal Server Error (500) / Service Unavailable (503) ──────────
  INTERNAL_SERVER_ERROR_500_001: { category: "server", retryable: false },
  SERVICE_UNAVAILABLE_503_001: { category: "server", retryable: true },
  // ══ Service-specific ErrorCatalog codes ═══════════════════════════════════
  // Each service registers its own namespaced codes with the platform
  // ErrorRegistry. They are business-rule violations unless their name says
  // otherwise (a `*_NOT_FOUND` is a not_found, an `ALREADY_*`/`*_EXISTS` is a
  // conflict, an `INVALID_*`/`*_MISMATCH` is a validation error), because that
  // is what decides the UI treatment.
  // ─── goals-service ─────────────────────────────────────────────
  GOAL_LIMIT_EXCEEDED: { category: "business", retryable: false },
  GOAL_NOT_ACTIVE: { category: "business", retryable: false },
  // ─── ledger-service ────────────────────────────────────────────
  ACCOUNT_NOT_ACTIVE: { category: "business", retryable: false },
  DUPLICATE_SINGLETON_ACCOUNT: { category: "conflict", retryable: false },
  INSUFFICIENT_LEDGER_BALANCE: { category: "business", retryable: false },
  INVALID_RECONCILIATION_STATE: { category: "validation", retryable: false },
  // R30: the customer saves in more than one unit and the request did not say which book it meant.
  // 'validation' because the fix is in the request — add ?unit=, or call the plural accounts read.
  LEDGER_UNIT_REQUIRED: { category: "validation", retryable: false },
  // INV-19: a gram posting aimed at a rupee account, or the reverse. Not retryable in any sense —
  // the same request will always be refused, and it should be.
  LEDGER_UNIT_MISMATCH: { category: "validation", retryable: false },
  // The tenant's ledger has no book in that unit yet. 'business' rather than 'validation': the
  // request is well formed, the tenant simply is not set up for it.
  LEDGER_UNIT_NOT_ENABLED: { category: "business", retryable: false },
  // D16: the credit would take this book past a cap the tenant's unit policy carries. 'business'
  // and not 'validation' — the request is perfectly well formed and will be well formed tomorrow;
  // what changed is that the tenant reached the exposure the platform agreed to let it run to.
  // Not retryable: retrying is exactly the wrong thing to do, and the fix is a person raising the
  // cap through the platform's override queue or verifying the account the cap is waiting on.
  UNIT_CAP_EXCEEDED: { category: "business", retryable: false },
  SUSPENSE_CASE_ALREADY_RESOLVED: { category: "conflict", retryable: false },
  // ─── loan-service ──────────────────────────────────────────────
  LOAN_ATTACHMENTS_NOT_CONFIGURED: { category: "business", retryable: false },
  LOAN_ATTACHMENT_INVALID: { category: "validation", retryable: false },
  LOAN_ATTACHMENT_INVALID_STATE: { category: "business", retryable: false },
  LOAN_ATTACHMENT_NOT_UPLOADED: { category: "business", retryable: false },
  LOAN_COMMENT_INVALID: { category: "validation", retryable: false },
  // Spreadsheet import (WP-7). All three are states the job is already in, not
  // problems with the request that just arrived — the wizard re-reads the job and
  // shows what it says rather than offering a retry that would answer the same way.
  LOAN_IMPORT_ALREADY_COMMITTED: { category: "conflict", retryable: false },
  LOAN_IMPORT_NOT_READY: { category: "conflict", retryable: false },
  // The one that IS about the request: too many rows for one synchronous check.
  LOAN_IMPORT_TOO_LARGE: { category: "validation", retryable: false },
  LOAN_LEAD_AMOUNT_TOO_LOW: { category: "business", retryable: false },
  LOAN_LEAD_INVALID_AMOUNT: { category: "business", retryable: false },
  LOAN_LEAD_INVALID_STATE: { category: "business", retryable: false },
  LOAN_PARTNER_BANK_INVALID: { category: "validation", retryable: false },
  LOAN_REDEMPTION_NOT_FOUND: { category: "not_found", retryable: false },
  // lender-service was unreachable, so the eligibility engine had no lender set to
  // evaluate against. The only code in this service that is genuinely worth retrying:
  // nothing about the request is wrong, and the next attempt may well land.
  LOAN_LENDER_DIRECTORY_UNAVAILABLE: { category: "business", retryable: true },
  // The eligibility engine's four, mapped here because contract:errors is one of the gates the
  // multi-lender change set has to run and these were the only other codes missing from it.
  // A rule's bounds and a profile's field values are both things the caller can correct, so
  // 'validation'; a RETIRED rule is a state that will not move, so 'business'; a code already
  // in use is the classic conflict — the same act succeeds under a different code.
  LOAN_ELIGIBILITY_RULE_INVALID: { category: "validation", retryable: false },
  LOAN_ELIGIBILITY_RULE_NOT_ACTIVE: { category: "business", retryable: false },
  LOAN_ELIGIBILITY_RULE_CODE_IN_USE: { category: "conflict", retryable: false },
  LOAN_APPLICANT_PROFILE_INVALID: { category: "validation", retryable: false },
  // Multi-lender filing. Each sends the desk somewhere different, which is why they are four
  // codes and not one — the categories are what a component branches on, and these three
  // branches are genuinely different screens.
  //
  // DUPLICATE_OPEN is 'conflict': the desk MAY file with that lender, just not while an earlier
  // application is still with them. Refresh, record the outcome of the one already there, and
  // the action becomes available — which is exactly what conflict means on this platform.
  LOAN_FILING_DUPLICATE_OPEN: { category: "conflict", retryable: false },
  // NOT_OPEN and ALREADY_DISBURSED are states that will not move on their own. Somebody else
  // recorded the outcome, or the case is funded; retrying is not the answer, reloading is.
  LOAN_FILING_NOT_OPEN: { category: "business", retryable: false },
  LOAN_FILING_ALREADY_DISBURSED: { category: "business", retryable: false },
  // The case is with the network financing desk, so its lenders are chosen there. Nothing about
  // the request is malformed and nothing local will make it work.
  LOAN_FILING_ON_NETWORK_LEAD: { category: "business", retryable: false },
  // ─── marketplace-service ───────────────────────────────────────
  // All three are the caller's problem to fix and none is retryable, which is what the categories
  // are for: 'business' would tell a moderator to try again, and the state they hit will not move
  // on its own.
  //
  // INVALID_STATE is deliberately NOT 'conflict'. Conflict on this platform means "you may do this,
  // but not you" — the maker-checker 409 next door, which a colleague resolves. Listing approval is
  // explicitly not maker-checker (D12), so a refused verb is about the listing's own state and no
  // second moderator changes the answer; the copy has to say "refresh", not "ask somebody else".
  MARKETPLACE_COLLECTION_INVALID: { category: "validation", retryable: false },
  MARKETPLACE_LISTING_INVALID: { category: "validation", retryable: false },
  MARKETPLACE_LISTING_INVALID_STATE: { category: "business", retryable: false },
  // ─── network-service ───────────────────────────────────────────
  // Case documents (WP-8), mirroring loan-service's attachment codes above because
  // they are the two halves of one exchange and a user meeting them on either side
  // should get the same treatment.
  CASE_DOCUMENTS_NOT_CONFIGURED: { category: "business", retryable: false },
  CASE_DOCUMENT_INVALID: { category: "validation", retryable: false },
  // NOT retryable, despite naming a transient: the desk's own action is finished
  // and the copy is a background sweep's job. Offering a retry button would invite
  // the user to re-drive an upload that already succeeded.
  CASE_DOCUMENT_NOT_UPLOADED: { category: "business", retryable: false },
  COUPON_NOT_REDEEMABLE: { category: "business", retryable: false },
  NETWORK_INVALID_STATE: { category: "business", retryable: false },
  NETWORK_PARTICIPATION_INACTIVE: { category: "business", retryable: false },
  SETTLEMENT_INVALID_STATE: { category: "business", retryable: false },
  // WP-13. Thrown as a ConflictException by IssueWabaGrantCommandHandler: a live grant over the
  // same number for the same operator already exists, so the caller's intent is already satisfied
  // and re-sending cannot change that — hence `conflict`, and not retryable, since a second
  // identical request fails identically. The fix is the other verb — amend the existing grant's
  // ceiling or expiry, or revoke it. WP-13 owns this line; if the sender screen wants different UI
  // treatment, change it here. (WP-7 added the same mapping to the cross-service block
  // at the foot of this file, because the catalogue gate was red on this code and WP-13 had
  // landed without an entry. One entry survives the merge, in the section the code belongs to.)
  WABA_GRANT_ALREADY_LIVE: { category: "conflict", retryable: false },
  // ─── redemption-service ────────────────────────────────────────
  REDEMPTION_ALREADY_TERMINAL: { category: "conflict", retryable: false },
  REDEMPTION_INVALID_STATE: { category: "business", retryable: false },
  // ─── scheme-service ────────────────────────────────────────────
  CONTRIBUTION_MODE_MISMATCH: { category: "validation", retryable: false },
  ENROLLMENT_LIMIT_REACHED: { category: "business", retryable: false },
  ENROLLMENT_NOT_ACTIVE: { category: "business", retryable: false },
  // The D5 cancellation refund (currency-ledger-design §4.3a). Both are business rules rather than
  // validation: the caller's request was well formed, and it is the plan's own arithmetic or the
  // refund's own state machine that refuses. Neither is retryable — retrying an over-cap refund
  // asks for the same too-large figure again, and a refund in the wrong state needs reloading, not
  // repeating.
  INVALID_REFUND_STATE: { category: "business", retryable: false },
  REFUND_EXCEEDS_REFUNDABLE: { category: "business", retryable: false },
  // 409 ConflictException: no card can be rendered for a CANCELLED enrollment.
  MEMBERSHIP_CARD_UNAVAILABLE: { category: "conflict", retryable: false },
  SCHEME_FINANCIAL_TERMS_LOCKED: { category: "business", retryable: false },
  SCHEME_NOT_ACTIVE: { category: "business", retryable: false },
  // ─── comms-service ─────────────────────────────────────────────
  NO_PROVIDER_CONFIGURED: { category: "business", retryable: false },
  // Channel provider-config codes, the comms half of the Integrations console.
  // `business` for the same reason as the payment/pricing blocks below: the
  // server's own sentence ("Wati rejected the connection test: …") is what the
  // drawer shows, and paraphrasing it here would lose the vendor's message.
  CREDENTIAL_NOT_PROVISIONED: { category: "business", retryable: false },
  CHANNEL_PROBE_FAILED: { category: "business", retryable: false },
  TEMPLATE_NOT_ACTIVE_ON_CHANNEL: { category: "business", retryable: false },
  WHATSAPP_TEMPLATE_NOT_APPROVED: { category: "business", retryable: false },
  // The template claimed a channel its producer never sends on (or SMS for a non-OTP message,
  // BR-16). A configuration mistake the author fixes in the form, not a runtime refusal.
  CHANNEL_NOT_PERMITTED_FOR_TEMPLATE: { category: "validation", retryable: false },
  // W1: this tenant's WhatsApp provider fronts its own template management (Wati, Twilio) and
  // cannot be asked to create one. 'business', not 'validation': nothing about the form is wrong,
  // and nothing about it can be corrected to make the submission work — the operator has to author
  // the template in the provider's own console and bind it here. Not retryable for the same reason.
  TEMPLATE_SUBMISSION_NOT_SUPPORTED: { category: "business", retryable: false },
  // 409 ConflictException: a template already holds this key in this scope.
  TEMPLATE_KEY_TAKEN: { category: "conflict", retryable: false },
  // The row is owned by campaign-service; the templates console may read it, not edit it.
  TEMPLATE_NOT_CONSOLE_WRITABLE: { category: "authorization", retryable: false },
  // The template declares a {{variable}} the producing service does not supply.
  TEMPLATE_VARIABLE_NOT_SUPPLIED: { category: "validation", retryable: false },
  // Sandbox test delivery was named on a server that will not run it — the feature flag is off,
  // or the environment label is outside the non-production allowlist. `business`, not `validation`:
  // the submitted value is well-formed and would be accepted on a dev stack, so the server's own
  // sentence ("Sandbox delivery … runs only in non-production deployments") is what the drawer
  // should show. In practice the catalogue endpoint never offers the option on such a server, so
  // seeing this code means a client holding a stale catalogue.
  SANDBOX_DELIVERY_NOT_AVAILABLE: { category: "business", retryable: false },
  // 409: WhatsApp only carries a free-form reply for 24 hours after the customer's own last
  // message, and that window has passed — or they have never written at all. `conflict` rather than
  // `business` because nothing about the request is wrong: the same words would have gone an hour
  // ago and will go again the moment the customer writes back. Not retryable, though, and that
  // pairing is the point: retrying changes nothing, the customer replying changes everything, so
  // the drawer should show the server's sentence (which names when the window closed) rather than
  // a Try again button.
  CONVERSATION_WINDOW_CLOSED: { category: "conflict", retryable: false },
  // ─── helpdesk-service ──────────────────────────────────────────
  TICKET_ALREADY_RATED: { category: "conflict", retryable: false },
  TICKET_ESCALATION_INVALID: { category: "validation", retryable: false },
  TICKET_INVALID_STATE_TRANSITION: { category: "business", retryable: false },
  // The PLATFORM desk's own aggregate (PlatformSupportCase). `business`, not `conflict`, for the
  // same reason TICKET_INVALID_STATE_TRANSITION is: the common cause is a case that has already
  // been answered, and the honest UI is "that is no longer available" rather than a retry prompt.
  // A second genuine verdict IS a conflict worth surfacing — the aggregate refuses it deliberately
  // — but the reader's next step is to reload the case, not to try again.
  PLATFORM_CASE_INVALID_STATE_TRANSITION: { category: "business", retryable: false },
  TICKET_NOT_ACTIVE: { category: "business", retryable: false },
  // Distinct from TICKET_ESCALATION_INVALID, which is the priority bump: this one refuses a
  // hand-off to QuickBee's own desk because the ticket is not classified TECHNICAL, or is already
  // escalated. `business`, not `validation` — the fix is a decision about the ticket, not a
  // malformed field.
  TICKET_NOT_ESCALATABLE_TO_PLATFORM: { category: "business", retryable: false },
  // The thread's audience matrix and body bounds. `business`, not `validation`: the two refusals
  // a user actually hits are "a requester's comment is always public" and "this ticket is not with
  // QuickBee, so it cannot be commented to their desk" — neither is a malformed field, and the
  // over-long body is caught client-side by the same 4000-character bound.
  TICKET_COMMENT_INVALID: { category: "business", retryable: false },
  // Help content (FAQ + knowledge base). The key clash is a `conflict` because
  // the author's fix is to pick another key; the state transition mirrors the
  // ticket one — a refusal the author has to resolve, not retry.
  ARTICLE_COLLECTION_KEY_TAKEN: { category: "conflict", retryable: false },
  ARTICLE_INVALID_STATE_TRANSITION: { category: "business", retryable: false },
  // ─── compliance-service ────────────────────────────────────────
  RULE_NOT_ACTIVE: { category: "business", retryable: false },
  TASK_NOT_ACTIONABLE: { category: "business", retryable: false },
  // ─── dispute-service ───────────────────────────────────────────
  DISPUTE_NOT_ACTIONABLE: { category: "business", retryable: false },
  // ─── identity-service ──────────────────────────────────────────
  DUPLICATE_ACCOUNT: { category: "conflict", retryable: false },
  KYC_INVALID_TRANSITION: { category: "business", retryable: false },
  KYC_NOT_PERMITTED: { category: "authorization", retryable: false },
  // Another reviewer already has this case open. `conflict`, and retryable: true — the claim
  // lapses on its own, so "try again shortly" is literally true rather than a platitude.
  KYC_CASE_CLAIMED: { category: "conflict", retryable: true },
  OTP_INVALID: { category: "validation", retryable: false },
  OTP_LOCKED: { category: "business", retryable: false },
  // An operator asked for a phone passcode against an account the assisted-verification leg
  // does not carry — a customer, whose code goes through assisted registration instead.
  PHONE_VERIFICATION_NOT_PERMITTED: { category: "authorization", retryable: false },
  // WP-9. No live delegation grant lets this tenant work inside the requested one — never
  // issued, revoked, or expired. `authorization` rather than `business`: the console's honest
  // response is to drop the delegated session and put the operator back in their own tenant,
  // which is what it does for any other "you may not" — and a retry cannot help, because the
  // thing that would fix it is somebody else pressing a button in their own console.
  DELEGATION_NOT_PERMITTED: { category: "authorization", retryable: false },
  // Delegation is switched off in this environment — identity-service holds no signing key.
  // `server`, not `authorization`: nobody did anything wrong and no grant would help, so the
  // message has to point at the deployment rather than at the operator. Not retryable either,
  // which is the part `server` does not imply on its own: a missing key is not a blip.
  DELEGATION_NOT_CONFIGURED: { category: "server", retryable: false },
  PASSWORD_INVALID: { category: "validation", retryable: false },
  // A referral/attribution code that matches no active agent in the tenant — the saver mistyped
  // it, or the agent has since been deactivated. Fixable in the form.
  AGENT_CODE_UNKNOWN: { category: "validation", retryable: false },
  // partner-service could not be reached to check the code; the attribution was NOT changed.
  AGENT_DIRECTORY_UNAVAILABLE: { category: "server", retryable: true },
  // The calling agent's own account carries no agent id, so it cannot register or act on a
  // customer. `business`, like AGENT_KYC_NOT_APPROVED below: the caller holds the grant, their
  // own account setup is what is incomplete. Not retryable — a partner administrator has to
  // finish provisioning the agent record first.
  AGENT_NOT_PROVISIONED: { category: "business", retryable: false },
  // The file that landed in storage is too large or the wrong type.
  KYC_DOCUMENT_INVALID: { category: "validation", retryable: false },
  // Confirm arrived before any file reached storage — the presigned link expired, or the upload
  // never started. Uploading again is the fix, so the code itself is retryable.
  KYC_DOCUMENT_NOT_UPLOADED: { category: "business", retryable: true },
  // No document store is configured in this environment; nothing the saver does changes that.
  KYC_DOCUMENT_STORAGE_UNAVAILABLE: { category: "server", retryable: false },
  // A submitted imageRef matches no confirmed upload — capture and upload again.
  KYC_IMAGE_REF_INVALID: { category: "validation", retryable: false },
  // The reset code is wrong, already used, or expired — request a new one.
  PASSWORD_RESET_INVALID: { category: "validation", retryable: false },
  // The identity provider behind this account cannot reset a password (a local/dev provider).
  PASSWORD_RESET_UNSUPPORTED: { category: "business", retryable: false },
  PASSWORD_TOO_WEAK: { category: "validation", retryable: false },
  // ─── lender-service ────────────────────────────────────────────
  LENDER_CODE_TAKEN: { category: "business", retryable: false },
  LENDER_INVALID_PROFILE: { category: "business", retryable: false },
  LENDER_INVALID_STATE: { category: "business", retryable: false },
  LENDER_NOT_ACTIVE: { category: "business", retryable: false },
  LENDER_NOT_FOUND: { category: "not_found", retryable: false },
  // ─── partner-service ───────────────────────────────────────────
  COMMISSION_PLAN_ENTRY_EXISTS: { category: "conflict", retryable: false },
  COMMISSION_RATE_INVALID: { category: "validation", retryable: false },
  COMMISSION_RATE_NOT_CONFIGURED: { category: "business", retryable: false },
  PARTNER_AGENT_ACCOUNT_CONFLICT: { category: "conflict", retryable: false },
  PARTNER_AGENT_EMAIL_TAKEN: { category: "business", retryable: false },
  PARTNER_AGENT_INVALID_STATE: { category: "business", retryable: false },
  PARTNER_AGENT_PROVISIONING_FAILED: { category: "business", retryable: false },
  // The passcode the partner read back did not match. Retryable by typing again, not by
  // replaying the request — the console mints a fresh attempt id per guess.
  PARTNER_AGENT_PHONE_OTP_INVALID: { category: "validation", retryable: false },
  // Pre-existing gap, not new here: this code has been thrown since V3 made the phone the
  // roster's key and never had an entry.
  PARTNER_AGENT_PHONE_TAKEN: { category: "business", retryable: false },
  // The generated agent code collided with an existing one (unique-index race) — retry the invite.
  AGENT_CODE_TAKEN: { category: "conflict", retryable: true },
  // No agent code can be minted: the tenant has no business code, or every code is spent. The
  // first is fixed in the admin console, the second by an operator — not by retrying.
  AGENT_CODE_UNAVAILABLE: { category: "business", retryable: false },
  PARTNER_CODE_TAKEN: { category: "business", retryable: false },
  PARTNER_INVALID_STATE: { category: "business", retryable: false },
  PAYOUT_ALREADY_GENERATED: { category: "conflict", retryable: false },
  PAYOUT_INVALID_STATE: { category: "business", retryable: false },
  REFERRAL_AGENT_NOT_ACTIVE: { category: "business", retryable: false },
  // The CDD gate (PK-01 / R-09). Thrown by BOTH partner-service (referrals, attribution, payouts)
  // and identity-service (assisted registration and assisted KYC) — one code for one rule, so a
  // console renders one message whichever half refused.
  //
  // `business`, not `forbidden`: the caller holds every grant the route requires, and their own
  // due diligence is the thing that is not approved yet. Rendering it as a permissions error sends
  // an operator to the role editor, which cannot fix it. Not retryable — it clears when a reviewer
  // approves the file, not when the request is sent again.
  AGENT_KYC_NOT_APPROVED: { category: "business", retryable: false },
  REFERRAL_ALREADY_SETTLED: { category: "conflict", retryable: false },
  REFERRAL_DISABLED: { category: "business", retryable: false },
  REFERRAL_INVALID_STATE: { category: "business", retryable: false },
  REFERRAL_PHONE_ALREADY_CLAIMED: { category: "conflict", retryable: false },
  // PK-72 — the number belongs to a customer already, so it can never convert.
  // Distinct from ALREADY_CLAIMED, which frees up when the other agent's claim expires.
  REFERRAL_PHONE_ALREADY_A_CUSTOMER: { category: "business", retryable: false },
  REFERRAL_PHONE_INVALID: { category: "validation", retryable: false },
  // ─── payment-service ───────────────────────────────────────────
  // Gateway provider-config codes (integration design §12.6). All `business`
  // (precedent: NO_PROVIDER_CONFIGURED) so the server's own sentence — "the
  // Razorpay key was rejected", "no gateway is configured for this tenant" —
  // reaches the console verbatim; the health badges are built on exactly that.
  GATEWAY_NOT_CONFIGURED: { category: "business", retryable: false },
  GATEWAY_PROBE_FAILED: { category: "business", retryable: false },
  // A platform-scope gateway configure while platform fallback is off (the platform
  // standard): a gateway is per tenant, so there is nothing at that scope to configure.
  PLATFORM_GATEWAY_NOT_ALLOWED: { category: "business", retryable: false },
  // Cashfree design D-6: the addressed vendor signs webhooks with its own API secret key, so
  // there is no separate secret to rotate. The console hides the affordance from
  // `webhookSecretIssuable: false`; this is the backstop for a caller that asks anyway.
  WEBHOOK_SECRET_NOT_ROTATABLE: { category: "business", retryable: false },
  // The tenant's configured gateway has no adapter deployed in this environment. Not retryable by
  // the client — nothing it can send changes the answer — and the server's sentence names the
  // vendor, which is what an operator needs.
  GATEWAY_VENDOR_UNAVAILABLE: { category: "business", retryable: false },
  // A `providerName` naming no gateway this platform integrates. Refused before any vault write,
  // because a fallback to the default vendor would store one vendor's secret under another's path.
  GATEWAY_PROVIDER_UNKNOWN: { category: "business", retryable: false },
  // Declared by BOTH PaymentErrorCatalog and PricingErrorCatalog (per-service
  // catalogs may duplicate a code); one entry here covers both wires.
  CONFIG_SUPERSEDED: { category: "business", retryable: false },
  // The collector is already carrying as much un-banked cash as this tenant permits (design §5.8 /
  // G11). `business`, and **retryable: true** — the only entry in this file where retrying is the
  // literal remedy rather than a hope: the position clears the moment the agent banks the cash and
  // declares the deposit, and the same request then succeeds unchanged. The server's own sentence
  // names both the amount and that remedy, which is why it reaches the screen verbatim.
  CASH_FLOAT_CEILING_EXCEEDED: { category: "business", retryable: true },
  // PK-84 — the collector is not an agent allowed to hold savers' cash. Not retryable:
  // nothing about the request changes it, and the fix is a compliance decision.
  COLLECTOR_NOT_PERMITTED: { category: "business", retryable: false },
  // ─── commerce-service ──────────────────────────────────────────
  // Fourteen 409s and eleven 422s (commerce-service 04-interfaces-layer.md §8). The
  // split follows ADR-014: a `conflict` is about the state the row is already
  // in and the caller resolves it by re-reading; a `validation` is about the
  // payload they sent. None is retryable — every one of them needs a different
  // request, not the same one again.
  COMMERCE_CATEGORY_KIND_MISMATCH: { category: "validation", retryable: false },
  COMMERCE_CATEGORY_NAME_TAKEN: { category: "conflict", retryable: false },
  COMMERCE_CATEGORY_NOT_EMPTY: { category: "conflict", retryable: false },
  COMMERCE_COUPON_CODE_TAKEN: { category: "conflict", retryable: false },
  COMMERCE_COUPON_DEFINITION_INVALID: { category: "validation", retryable: false },
  COMMERCE_COUPON_ILLEGAL_TRANSITION: { category: "conflict", retryable: false },
  COMMERCE_COUPON_NOT_REDEEMABLE: { category: "conflict", retryable: false },
  COMMERCE_LOYALTY_INSUFFICIENT_POINTS: { category: "validation", retryable: false },
  COMMERCE_LOYALTY_MEMBER_ERASED: { category: "conflict", retryable: false },
  COMMERCE_LOYALTY_PROGRAM_PAUSED: { category: "conflict", retryable: false },
  COMMERCE_LOYALTY_REDEEM_EXCEEDS_BILL: { category: "validation", retryable: false },
  COMMERCE_LOYALTY_TIERS_INVALID: { category: "validation", retryable: false },
  // T-01, the catalogue import. The first two are about the job's own state and the wizard
  // resolves both by re-reading it.
  COMMERCE_IMPORT_ALREADY_COMMITTED: { category: "conflict", retryable: false },
  COMMERCE_IMPORT_NOT_READY: { category: "conflict", retryable: false },
  // A row that passed the dry-run and no longer applies: the catalogue moved under the merchant,
  // so the whole commit rolled back. `conflict` rather than `validation` because nothing they
  // typed is wrong — the fix is to re-run the dry-run, which the message says.
  COMMERCE_IMPORT_ROW_STALE: { category: "conflict", retryable: false },
  // More rows than one synchronous dry-run may honestly validate. A payload problem they fix by
  // splitting the file, so 'validation' and not 'server'.
  COMMERCE_IMPORT_TOO_LARGE: { category: "validation", retryable: false },
  // §9 B14. The deployment has no bucket wired for storefront images: nothing the merchant typed
  // is wrong and retrying changes nothing, so it is 'server' and not 'validation' — a form-field
  // error would send them looking for a mistake they did not make.
  COMMERCE_MEDIA_STORAGE_UNAVAILABLE: { category: "server", retryable: false },
  COMMERCE_OFFER_ILLEGAL_TRANSITION: { category: "conflict", retryable: false },
  // The picture's type, size or count against the deployment's media policy — the merchant can
  // fix all three.
  COMMERCE_OFFER_MEDIA_INVALID: { category: "validation", retryable: false },
  COMMERCE_OFFER_NOT_APPLICABLE: { category: "validation", retryable: false },
  COMMERCE_OFFER_NOT_EDITABLE: { category: "conflict", retryable: false },
  // The card is missing or its wording is too long. 'validation' rather than 'conflict': pushing
  // without a headline is an unfinished form, not a state that moved under the caller.
  COMMERCE_OFFER_STOREFRONT_INVALID: { category: "validation", retryable: false },
  COMMERCE_PHONE_INVALID: { category: "validation", retryable: false },
  COMMERCE_PRODUCT_ATTRIBUTES_INVALID: { category: "validation", retryable: false },
  COMMERCE_PRODUCT_ILLEGAL_TRANSITION: { category: "conflict", retryable: false },
  COMMERCE_RATE_CARD_NOT_DRAFT: { category: "conflict", retryable: false },
  COMMERCE_RATE_CARD_TERMS_INVALID: { category: "validation", retryable: false },
  COMMERCE_SCOPE_INVALID: { category: "validation", retryable: false },
  COMMERCE_SKU_TAKEN: { category: "conflict", retryable: false },
  COMMERCE_WINDOW_INVALID: { category: "validation", retryable: false },
  // ─── pricing-service ───────────────────────────────────────────
  PRICING_NO_ACTIVE_OVERRIDE: { category: "business", retryable: false },
  // Feed provider-config codes (integration design §12.6) — `business` for the
  // same verbatim-message reason as the payment-service block above.
  // (`CONFIG_SUPERSEDED` is shared with payment-service and mapped there.)
  FEED_NOT_CONFIGURED: { category: "business", retryable: false },
  FEED_PROBE_FAILED: { category: "business", retryable: false },
  // ─── reporting-service ─────────────────────────────────────────
  REPORT_EXPIRED: { category: "business", retryable: false },
  REPORT_FAILED: { category: "business", retryable: false },
  REPORT_NOT_READY: { category: "business", retryable: false },
  REPORT_STATE_INVALID: { category: "validation", retryable: false },
  // ─── tenant-service ────────────────────────────────────────────
  BRANCH_HAS_ACTIVE_STAFF: { category: "business", retryable: false },
  INVALID_TENANT_TRANSITION: { category: "validation", retryable: false },
  LAST_ACTIVE_ADMIN: { category: "business", retryable: false },
  SCHEMA_PROVISIONING_FAILED: { category: "business", retryable: false },
  // The roster row was written; the login was not. `retryable: false` because retrying the same
  // request is not what fixes it — re-inviting the same email is, and that is a deliberate act
  // the service's own sentence describes. An automatic retry would look identical to the
  // operator and change nothing.
  STAFF_USER_PROVISIONING_FAILED: { category: "business", retryable: true },
  // Never reaches a browser — the slice it comes from takes no user token — but the catalogue is
  // asserted complete against every backend code, and a gap there is a generic server error on
  // the screen rather than a designed one.
  STAFF_S2S_ROLE_REFUSED: { category: "business", retryable: false },
  // Ordering, not a failure: offboard the staff member, then erase. Retrying the erasure before
  // that step is refused identically.
  ERASURE_BLOCKED_BY_STAFF_GRANTS: { category: "business", retryable: false },
  // The same number, refused the same way, every time — until either a different number is used
  // or the existing account is migrated. Nothing about retrying changes it.
  PRINCIPAL_BELONGS_TO_ANOTHER_TENANT: { category: "business", retryable: false },
  // The grant is recorded; there is no principal to mirror it onto, so no token will carry it.
  // Retrying grants it again onto the same principal-less account — the login has to be
  // re-provisioned first, which is a different action.
  ACCOUNT_HAS_NO_PRINCIPAL: { category: "business", retryable: false },
  // A pool misconfiguration, not a refusal: the same call fails the same way until somebody
  // enables software-token MFA on the pool.
  MFA_NOT_AVAILABLE: { category: "business", retryable: false },
  // The caller owns their security settings; this particular one is not theirs to drop while
  // they hold the role. 'conflict' rather than 'business' so the screen reads it as a state
  // clash rather than as missing authority.
  MFA_REQUIRED_FOR_ROLE: { category: "conflict", retryable: false },
  // Identity declined rather than failed to answer: the same request is refused the same way
  // every time, so offering a retry would only add audit rows.
  STAFF_USER_PROVISIONING_BLOCKED: { category: "business", retryable: false },
  // Subscription-plan catalogue. All three are 'conflict' or 'validation' rather than 'business',
  // because each names something the operator can see and fix on the Plans screen — a taken code,
  // a plan whose status moved on, a tier that is not published. Category copy would replace the
  // service's own sentence with something less useful.
  DUPLICATE_PLAN_CODE: { category: "conflict", retryable: false },
  INVALID_PLAN_TRANSITION: { category: "conflict", retryable: false },
  PLAN_NOT_ASSIGNABLE: { category: "validation", retryable: false },
  // ── Tenant documents ────────────────────────────────────────────────────────
  // Every one of these is thrown as a UserFriendlyException carrying its own message and remedy, so
  // the category picks the UI treatment only — it must not cause the console to substitute generic
  // copy for the sentence the service wrote.
  TENANT_DOCUMENT_INVALID: { category: "validation", retryable: false },
  // "Retry the upload — the link may have expired before the file finished sending."
  TENANT_DOCUMENT_NOT_UPLOADED: { category: "conflict", retryable: true },
  TENANT_DOCUMENT_NOT_AVAILABLE: { category: "business", retryable: false },
  TENANT_DOCUMENT_NOT_PUBLISHABLE: { category: "business", retryable: false },
  // The file is the tenant's current logo — clear the logo first. A state the operator can resolve.
  TENANT_DOCUMENT_IN_USE: { category: "conflict", retryable: false },
  // S3 is down, not the operator's doing.
  TENANT_DOCUMENT_STORAGE_UNAVAILABLE: { category: "server", retryable: true },
  // ── CRM ─────────────────────────────────────────────────────────────────────
  // INV-5: a mirror field (name, phone) on a PLATFORM-sourced row. Not malformed input — the field
  // is owned elsewhere — so 'business', not 'validation'.
  CRM_FIELD_NOT_EDITABLE: { category: "business", retryable: false },
  CRM_CUSTOMER_ARCHIVED: { category: "business", retryable: false },
  CRM_PHONE_INVALID: { category: "validation", retryable: false },
  CRM_PURCHASE_TOTAL_MISMATCH: { category: "validation", retryable: false },
  CRM_SEGMENT_TYPE_MISMATCH: { category: "validation", retryable: false },
  CRM_SEGMENT_RULES_INVALID: { category: "validation", retryable: false },
  CRM_SEGMENT_NAME_TAKEN: { category: "conflict", retryable: false },
  CRM_IMPORT_TOO_LARGE: { category: "validation", retryable: false },
  // Both 409s, and both are about the job's state rather than the request's shape.
  CRM_IMPORT_NOT_READY: { category: "conflict", retryable: false },
  CRM_IMPORT_ALREADY_COMMITTED: { category: "conflict", retryable: false },
  // A 409, and about the row's state: somebody else linked this record to a different QuickBee
  // account. Linking it to the one it ALREADY holds is a 200 — the registration event legitimately
  // wins that race with the console — so this code only ever means a genuine disagreement.
  CRM_CUSTOMER_ALREADY_LINKED: { category: "conflict", retryable: false },
  // The account being linked does not hold this row's number. 'business', not 'validation': the
  // request is well formed and the caller holds the permission — what is wrong is that these are
  // two different people, which is the one thing this check exists to catch.
  CRM_CUSTOMER_LINK_PHONE_MISMATCH: { category: "business", retryable: false },
  // ── Pulse cockpit ───────────────────────────────────────────────────────────
  PULSE_COMPONENT_NOT_FOUND: { category: "not_found", retryable: false },
  PULSE_UNKNOWN_COMPONENT_REFERENCE: { category: "validation", retryable: false },
  PULSE_COMPONENT_KEY_TAKEN: { category: "conflict", retryable: false },
  PULSE_SELF_DEPENDENCY: { category: "validation", retryable: false },
  PULSE_COMPONENT_RETIRED: { category: "business", retryable: false },
  PULSE_INVALID_INCIDENT_TRANSITION: { category: "conflict", retryable: false },
  PULSE_INVALID_RELEASE_TRANSITION: { category: "conflict", retryable: false },
  PULSE_RUN_TERMINAL: { category: "conflict", retryable: false },
  PULSE_INVALID_OBJECTIVE: { category: "validation", retryable: false },
  PULSE_OBSERVATION_IMMUTABLE: { category: "business", retryable: false },
  // The three genuinely transient ones. A sweep finishes, an executor comes back, and an optimistic
  // -lock clash is resolved by re-reading and re-submitting — retrying any of these can succeed,
  // which is the whole point of the flag being independent of the HTTP status.
  PULSE_SWEEP_IN_PROGRESS: { category: "conflict", retryable: true },
  PULSE_EXECUTOR_UNAVAILABLE: { category: "server", retryable: true },
  // A DLQ sample could not be fetched from the queue just now; the console can ask again.
  PULSE_DLQ_SAMPLE_UNAVAILABLE: { category: "server", retryable: true },
  // The sync payload names an environment other than the one this pulse serves.
  PULSE_ENVIRONMENT_MISMATCH: { category: "validation", retryable: false },
  PULSE_CONCURRENT_MODIFICATION: { category: "conflict", retryable: true },
  // ─── engagement-service ────────────────────────────────────────
  // The public plane. Seven codes, and the interesting thing about them is how little they say.
  //
  // ENGAGEMENT_LINK_UNAVAILABLE and ENGAGEMENT_SESSION_INVALID are ONE refusal wearing two names:
  // both carry the identical sentence, and the backend deliberately flattens expired, spent,
  // revoked, forged and unknown tokens into them — in body and in response time
  // (marketing-engine-solution-design.md §4.8). Two codes exist so a meter can separate "bad
  // link" from "bad transition"; a component that branched between them would be reading noise,
  // and one that tried to explain WHICH link failure occurred would be rebuilding the
  // enumeration oracle the flattening removes.
  //
  // `business`, not `not_found`: a 404 treatment sends a customer looking for a typo in a URL
  // they never typed, and this is a neutral end state rather than a missing resource.
  // Non-retryable, because none of the five states becomes usable by asking again.
  ENGAGEMENT_LINK_UNAVAILABLE: { category: "business", retryable: false },
  ENGAGEMENT_SESSION_INVALID: { category: "business", retryable: false },
  // The one public failure that IS explained, and the only one a customer can act on: it
  // restates the band the page already displayed, so it reveals nothing the holder of the link
  // did not already have.
  ENGAGEMENT_AMOUNT_OUT_OF_BOUNDS: { category: "validation", retryable: false },
  // S2S only — campaign-service's dispatch sees this, never a customer.
  ENGAGEMENT_LINK_NOT_MINTABLE: { category: "business", retryable: false },
  // WP-7's three, and the test for each is the same as for the four above: does it say anything
  // about the TOKEN? None of them does, which is why they are allowed to be distinguishable.
  //
  // The business behind this link is not taking applications — no financing enrolment, no
  // published wording, or the projection holding both could not be read. True of every token that
  // resolves to this tenant, so it maps no token space. `business` rather than `server` even for
  // the unreachable-projection case: the customer's experience is identical to a jeweller who
  // sells no loans, and a "try again" treatment would promise something that is not coming.
  ENGAGEMENT_APPLY_UNAVAILABLE: { category: "business", retryable: false },
  // The operator published new consent wording while the form was open (Q-1 on the public plane).
  // Retryable, and the only code here that is: the page re-reads the wording and asks again, which
  // is the one outcome where the consent record says what the person actually agreed to.
  ENGAGEMENT_CONSENT_CHANGED: { category: "conflict", retryable: true },
  // Something on the form cannot be stored — a phone number that is not one, most often. The
  // customer can fix it, so `validation`; the message never echoes what they typed.
  ENGAGEMENT_APPLICANT_INVALID: { category: "validation", retryable: false },
  // Activation was asked for on a tenant nobody can sign into. `conflict`, not `validation`:
  // nothing in the request is wrong and the very same request succeeds untouched once the tenant
  // has an administrator with a login — which is a thing the operator does elsewhere, on the
  // Users tab, not a field they can correct here. Retrying without doing that repeats identically.
  TENANT_HAS_NO_ADMINISTRATOR: { category: "conflict", retryable: false },
  // ── Cross-service ───────────────────────────────────────────────────────────
  // INV-17 maker-checker: the approver is the submitter. Thrown as a ConflictException.
  SELF_APPROVAL_FORBIDDEN: { category: "conflict", retryable: false },
  // The other half of four eyes: the reviewer is the user who registered the subject. Distinct
  // from the code above because the remedy differs — this one is resolved by handing the case to
  // any other reviewer, not by finding someone other than the customer.
  REGISTRAR_CANNOT_DECIDE: { category: "conflict", retryable: false },
  // The customer's KYC standing does not permit money movement — their review fell due, a
  // re-review refused them, or an erasure restricted the account. Not retryable: nothing about
  // the request changes the answer, only the customer completing their KYC does.
  REDEMPTION_KYC_NOT_TRANSACTABLE: { category: "validation", retryable: false },
  // Core HTTP-level code from the SDK's ErrorCode enum, not a domain rule: the caller used the
  // wrong verb, and no amount of retrying the same request fixes it.
  METHOD_NOT_ALLOWED_405_001: { category: "server", retryable: false }
};
var UNKNOWN_ERROR_META = { category: "unknown", retryable: false };
function getErrorCodeMeta(code) {
  return errorCodeMap[code] ?? UNKNOWN_ERROR_META;
}

// src/errors/ApiError.ts
var ApiError = class _ApiError extends Error {
  /** HTTP status the error travelled on. `0` for a network failure that never reached the server. */
  status;
  /** Machine-readable code — mirrors a `quickbee-sdk-core` `ErrorCode` name, or a service-specific `ErrorCatalog` code (§4.3). */
  code;
  /** Dot-notation field path for field-level validation errors; `null` for non-field errors. */
  field;
  /** Any additional error entries beyond the first, when the envelope carried more than one (e.g. multi-field validation). */
  details;
  /** `meta.requestId` from the response envelope, for support-ticket correlation. */
  requestId;
  /** The full, unmodified `errors[]` array from the response envelope. */
  errors;
  /**
   * The response's `Retry-After`, in whole seconds — `undefined` when the server did not send
   * one, which is every status but `429` and some `503`s.
   *
   * The header is the only place the wait is stated: `RateLimitExceededException` carries
   * `retryAfterSeconds` and the backend's `GlobalExceptionHandler` puts it on `Retry-After`, but
   * the JSON envelope below it says only "Rate limit exceeded". Without this field a screen that
   * wants to render a refusal as a countdown has two options, both wrong: parse the English out
   * of `message`, or hard-code the interval and drift from the server the first time ops retunes
   * it. The OTP resend controls (`@quickbee/components`' `useResendCooldown`) count this down.
   *
   * Parsed permissively and never invented: a delta-seconds value is taken as-is, an HTTP-date is
   * converted against the local clock and clamped at zero, and anything unparseable leaves this
   * `undefined` so the caller falls back to showing the error.
   */
  retryAfterSeconds;
  constructor(options) {
    super(options.message, options.cause !== void 0 ? { cause: options.cause } : void 0);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.field = options.field ?? null;
    this.details = options.details;
    this.requestId = options.requestId;
    this.errors = options.errors ?? [];
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
  /** Category derived from `errorCodeMap.ts`, kept in lockstep with the backend `ErrorCode` catalogue (§4.3). */
  get category() {
    return getErrorCodeMeta(this.code).category;
  }
  /**
   * Whether `errorCodeMap.ts` treats this code as transient. Informational
   * only — the client's own retry loop decides purely from HTTP status
   * (`429`/`503`, §4.3), not from this flag, so this never causes a retry by
   * itself.
   */
  get retryable() {
    return getErrorCodeMeta(this.code).retryable;
  }
  /** Builds an `ApiError` from a parsed `{ errors, meta }` envelope (§4.3), using the first entry as the primary error. */
  static fromErrorEnvelope(envelope, status, retryAfterSeconds) {
    const [first, ...rest] = envelope.errors;
    return new _ApiError({
      status,
      code: first?.code ?? "UNKNOWN_ERROR",
      message: first?.message ?? "An unknown error occurred",
      field: first?.field ?? null,
      requestId: envelope.meta?.requestId,
      errors: envelope.errors,
      ...retryAfterSeconds === void 0 ? {} : { retryAfterSeconds },
      ...rest.length > 0 ? { details: rest } : {}
    });
  }
  /** Builds an `ApiError` for a request that never reached the server (§4.3: distinct from a `5xx` the server actually returned). */
  static network(message, cause) {
    return new _ApiError({ status: 0, code: "NETWORK_ERROR", message, cause });
  }
  /**
   * Builds an `ApiError` for a **2xx** whose body is not the JSON envelope the
   * request asked for — a CDN/SPA fallback `index.html`, a proxy error page, a
   * `text/plain` body from a gateway that matched the wrong route.
   *
   * This case used to be silent: the parse failure resolved as `undefined`,
   * `unwrapEnvelope` passed it through, and `@quickbee/store`'s `baseQuery`
   * turned it into `{ data: null }` — an RTK Query **success**. The screen
   * rendered "no results" with no error and nothing to debug, so an HTML
   * fallback was indistinguishable from a legitimately empty list.
   *
   * Carries the context, method, URL, status and received `Content-Type`
   * because those four are what identify the misroute; the status is kept as
   * the real `2xx` rather than being rewritten to `0`/`500`, so a reader sees
   * "a 200 that wasn't JSON", which is the actual fault.
   */
  static malformedResponse(options) {
    const { status, method, url, context, contentType, bodyPreview, cause } = options;
    return new _ApiError({
      status,
      code: "MALFORMED_RESPONSE",
      message: `[api-client] ${context}: ${method} ${url} answered ${status} with a body that is not JSON (content-type: ${contentType ?? "none"}). This is a CDN/SPA fallback page or a misrouted gateway path, not an empty result \u2014 see backend-api-integration-design.md \xA74.3. Body starts: ${bodyPreview}`,
      details: { context, method, url, contentType, bodyPreview },
      ...cause !== void 0 ? { cause } : {}
    });
  }
};

// src/errors/retryAfter.ts
function parseRetryAfter(header, now = Date.now()) {
  if (header === null || header === void 0) return void 0;
  const raw = header.trim();
  if (!raw) return void 0;
  if (/^\d+$/.test(raw)) {
    return Number(raw);
  }
  if (/^[+-]?\d*\.?\d+$/.test(raw)) return void 0;
  const at = Date.parse(raw);
  if (Number.isNaN(at)) return void 0;
  return Math.max(0, Math.ceil((at - now) / 1e3));
}
var DEFAULT_LOCALE = "en-IN";
var IDEMPOTENCY_HEADER = "X-Idempotency-Key";
var BRANCH_HEADER = "X-Branch-ID";
function buildRequestHeaders(options) {
  const {
    authStore,
    context,
    appName,
    getLocale,
    extraHeaders,
    publicRoute,
    ownSession,
    contextIsDelegable,
    hasBody
  } = options;
  const headers = new Headers();
  if (hasBody) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("X-Correlation-ID", `${context}:${generateCorrelationId()}`);
  headers.set("Accept-Language", getLocale?.() ?? DEFAULT_LOCALE);
  if (publicRoute || !authStore) {
    return applyExtraHeaders(headers, extraHeaders);
  }
  const state = authStore.getState();
  const delegation = !ownSession && contextIsDelegable !== false ? state.delegation ?? null : null;
  const accessToken = delegation?.token ?? state.accessToken;
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const tenantId = delegation?.tenantId ?? state.user?.tenantId ?? state.tenantConfig?.tenantId;
  if (tenantId) {
    headers.set("X-Tenant-ID", tenantId);
  }
  if (state.activeBranch && !delegation) {
    headers.set(BRANCH_HEADER, state.activeBranch);
  }
  if (state.user?.sessionId && !delegation) {
    headers.set("X-Device-Session-Id", state.user.sessionId);
  }
  if (appName) {
    headers.set("X-App-Name", appName);
  }
  return applyExtraHeaders(headers, extraHeaders);
}
function applyExtraHeaders(headers, extraHeaders) {
  if (extraHeaders) {
    for (const [key, value] of Object.entries(extraHeaders)) {
      headers.set(key, value);
    }
  }
  return headers;
}

// src/interceptors/requestDeduplication.ts
function buildDedupeKey(input) {
  const sortedParams = input.params ? Object.fromEntries(Object.entries(input.params).sort(([a], [b]) => a.localeCompare(b))) : {};
  return `${input.method}:${input.url}:${JSON.stringify(sortedParams)}`;
}
var RequestDeduplicator = class {
  inFlight = /* @__PURE__ */ new Map();
  /** Returns the already-in-flight promise for `key` if one exists; otherwise starts one via `factory` and tracks it until it settles. */
  dedupe(key, factory) {
    const existing = this.inFlight.get(key);
    if (existing) {
      return existing;
    }
    const promise = factory().finally(() => {
      this.inFlight.delete(key);
    });
    this.inFlight.set(key, promise);
    return promise;
  }
};
var RETRYABLE_STATUSES = /* @__PURE__ */ new Set([429, 503]);
var DEFAULT_MAX_RETRIES = 2;
function shouldRetry(input) {
  if (!RETRYABLE_STATUSES.has(input.status)) return false;
  if (input.attempt >= input.maxRetries) return false;
  return input.method === "GET" || Boolean(input.explicitlyRetryable);
}
function delayForRetry(attempt) {
  const delay = computeBackoffDelay(attempt);
  return new Promise((resolve) => setTimeout(resolve, delay));
}

// src/factory/createApiClient.ts
var IDEMPOTENT_IN_PROGRESS = "IDEMPOTENT_REQUEST_IN_PROGRESS_409_003";
function buildUrl(baseUrl, path, params) {
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path.replace(/^\/+/, "");
  const queryIndex = trimmedPath.indexOf("?");
  const pathOnly = queryIndex === -1 ? trimmedPath : trimmedPath.slice(0, queryIndex);
  const inlineQuery = queryIndex === -1 ? "" : trimmedPath.slice(queryIndex + 1);
  const searchParams = new URLSearchParams(inlineQuery);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === void 0 || value === null) continue;
      searchParams.set(key, String(value));
    }
  }
  const queryString = searchParams.toString();
  return `${trimmedBase}/${pathOnly}${queryString ? `?${queryString}` : ""}`;
}
async function safeParseJson(response) {
  try {
    return await response.json();
  } catch {
    return void 0;
  }
}
var BODY_PREVIEW_LIMIT = 120;
function previewBody(raw) {
  const collapsed = raw.replace(/\s+/g, " ").trim();
  return collapsed.length > BODY_PREVIEW_LIMIT ? `${JSON.stringify(collapsed.slice(0, BODY_PREVIEW_LIMIT))}\u2026` : JSON.stringify(collapsed);
}
async function readSuccessBody(response, diagnostics) {
  let raw;
  try {
    raw = await response.text();
  } catch (cause) {
    throw ApiError.network("Response body could not be read", cause);
  }
  if (raw.trim() === "") return void 0;
  try {
    return JSON.parse(raw);
  } catch (cause) {
    const error = ApiError.malformedResponse({
      status: response.status,
      contentType: response.headers.get("content-type"),
      bodyPreview: previewBody(raw),
      ...diagnostics,
      cause
    });
    if (isDevelopmentBuild()) console.error(error.message);
    throw error;
  }
}
async function toApiError(response) {
  const body = await safeParseJson(response);
  const retryAfterSeconds = parseRetryAfter(response.headers.get("retry-after"));
  if (isErrorEnvelope(body)) {
    return ApiError.fromErrorEnvelope(body, response.status, retryAfterSeconds);
  }
  return new ApiError({
    status: response.status,
    code: "UNKNOWN_ERROR",
    message: response.statusText || `Request failed with status ${response.status}`,
    ...retryAfterSeconds === void 0 ? {} : { retryAfterSeconds }
  });
}
function isDevelopmentBuild() {
  return typeof process !== "undefined" && typeof process.env !== "undefined" && process.env["NODE_ENV"] !== "production";
}
function createApiClient(config) {
  const {
    baseUrl,
    context,
    appName,
    authStore,
    onAuthFailure,
    getLocale,
    fetchImpl = fetch,
    maxRetries = DEFAULT_MAX_RETRIES,
    onResponseMeta,
    refreshCoordinator = sharedRefreshCoordinator
  } = config;
  const deduplicator = new RequestDeduplicator();
  const warnedGeneratedKeys = /* @__PURE__ */ new Set();
  const delegableContext = isDelegableContext(context) && usesGatewayPathKey();
  function routesThroughDelegatedPlane(args) {
    if (!delegableContext || args.publicRoute || args.ownSession || !authStore) return false;
    return authStore.getState().delegation != null;
  }
  function resolveAppName() {
    return typeof appName === "function" ? appName() : appName;
  }
  function applyIdempotencyHeader(headers, method, args) {
    if (method === "GET" || args.skipIdempotencyKey) return;
    if (args.idempotencyKey) {
      headers.set(IDEMPOTENCY_HEADER, args.idempotencyKey);
      return;
    }
    if (args.allowGeneratedKey) {
      headers.set(IDEMPOTENCY_HEADER, generateCorrelationId());
      return;
    }
    if (isDevelopmentBuild()) {
      const signature = `${method} ${args.url}`;
      if (!warnedGeneratedKeys.has(signature)) {
        warnedGeneratedKeys.add(signature);
        console.warn(
          `[api-client] ${signature} is a mutation with no idempotencyKey. Supply a caller-stable key (e.g. "enroll:{schemeId}:{draftId}") so a double submit cannot become two writes, or set skipIdempotencyKey for a non-@IdempotentRequest endpoint \u2014 backend-api-integration-design.md I9.`
        );
      }
    }
  }
  async function performRequest(args, attempt, hasRefreshed, idempotencyRetried) {
    const method = args.method ?? "GET";
    const delegated = routesThroughDelegatedPlane(args);
    const url = buildUrl(baseUrl, delegated ? `${DELEGATED_PATH_SEGMENT}/${args.url.replace(/^\/+/, "")}` : args.url, args.params);
    const resolvedAppName = resolveAppName();
    const headers = buildRequestHeaders({
      authStore,
      context: String(context),
      ...resolvedAppName ? { appName: resolvedAppName } : {},
      ...getLocale ? { getLocale } : {},
      ...args.headers ? { extraHeaders: args.headers } : {},
      ...args.publicRoute ? { publicRoute: true } : {},
      ...args.ownSession ? { ownSession: true } : {},
      contextIsDelegable: delegableContext,
      hasBody: args.body !== void 0
    });
    applyIdempotencyHeader(headers, method, args);
    let response;
    try {
      response = await fetchImpl(url, {
        method,
        headers,
        // §6.2: the API surface is authenticated purely by the Authorization
        // header under D1. The only cookie-authenticated call in the platform is
        // POST /auth/refresh, which `@quickbee/auth` issues itself with
        // credentials: 'include'. Sending credentials here would widen the CORS
        // requirement for no benefit.
        credentials: "omit",
        ...args.signal ? { signal: args.signal } : {},
        ...args.body !== void 0 ? { body: JSON.stringify(args.body) } : {}
      });
    } catch (cause) {
      throw ApiError.network("Network request failed", cause);
    }
    if (response.status === 401) {
      if (hasRefreshed || args.publicRoute || !authStore) {
        onAuthFailure?.();
        throw await toApiError(response);
      }
      const refreshed = await refreshCoordinator.attemptRefresh({
        authStore,
        onAuthFailure: onAuthFailure ?? (() => {
        }),
        ...config.fetchImpl ? { fetchImpl: config.fetchImpl } : {}
      });
      if (refreshed) {
        return performRequest(args, attempt, true, idempotencyRetried);
      }
      throw await toApiError(response);
    }
    if (shouldRetry({
      status: response.status,
      method,
      attempt,
      maxRetries,
      ...args.retryable !== void 0 ? { explicitlyRetryable: args.retryable } : {}
    })) {
      await delayForRetry(attempt);
      return performRequest(args, attempt + 1, hasRefreshed, idempotencyRetried);
    }
    if (response.status === 409 && !idempotencyRetried) {
      const error = await toApiError(response);
      if (error.code === IDEMPOTENT_IN_PROGRESS) {
        await delayForRetry(attempt);
        return performRequest(args, attempt, hasRefreshed, true);
      }
      throw error;
    }
    if (!response.ok) {
      throw await toApiError(response);
    }
    if (response.status === 204 || response.status === 205) {
      return void 0;
    }
    if (args.responseType === "binary") {
      return await toFileDownload(response, url);
    }
    const body = await readSuccessBody(response, { method, url, context: String(context) });
    if (onResponseMeta) {
      const meta = readResponseMeta(body);
      if (meta) onResponseMeta(meta, { url, method, status: response.status });
    }
    return unwrapEnvelope(body);
  }
  function request(args) {
    const method = args.method ?? "GET";
    if (method === "GET") {
      const key = buildDedupeKey({
        method,
        url: args.url,
        ...args.params ? { params: args.params } : {}
      });
      return deduplicator.dedupe(key, () => performRequest(args, 0, false, false));
    }
    return performRequest(args, 0, false, false);
  }
  return {
    request,
    get: (url, options) => request({ ...options, url, method: "GET" }),
    post: (url, body, options) => request({ ...options, url, method: "POST", body }),
    put: (url, body, options) => request({ ...options, url, method: "PUT", body }),
    patch: (url, body, options) => request({ ...options, url, method: "PATCH", body }),
    delete: (url, options) => request({ ...options, url, method: "DELETE" })
  };
}
function defaultLocale() {
  if (typeof document !== "undefined" && document.documentElement.lang) {
    return document.documentElement.lang;
  }
  return "en-IN";
}
var warnedUnreachable = /* @__PURE__ */ new Set();
function isDevelopmentBuild2() {
  return typeof process !== "undefined" && typeof process.env !== "undefined" && process.env["NODE_ENV"] !== "production";
}
function createContextClient(config) {
  const { context, publicClient } = config;
  const descriptor = getContextDescriptor(context);
  if (!descriptor.reachable && isDevelopmentBuild2() && !warnedUnreachable.has(context)) {
    warnedUnreachable.add(context);
    console.warn(
      `[api-client] Bounded context "${context}" has no deployed gateway stack. Every request to it will fail explicitly (\xA716 B5).`
    );
  }
  let live = null;
  function transport() {
    if (live) return live;
    const baseUrl = resolveContextBaseUrl(context);
    live = createApiClient({
      baseUrl,
      context,
      appName: config.appName ?? (() => getActiveApp()),
      authStore: publicClient ? null : config.authStore ?? useAuthStore,
      getLocale: config.getLocale ?? defaultLocale,
      ...config.onAuthFailure ? { onAuthFailure: config.onAuthFailure } : {},
      ...config.fetchImpl ? { fetchImpl: config.fetchImpl } : {},
      ...config.maxRetries !== void 0 ? { maxRetries: config.maxRetries } : {},
      ...config.onResponseMeta ? { onResponseMeta: config.onResponseMeta } : {}
    });
    return live;
  }
  return {
    request: async (args) => transport().request(args),
    get: async (url, options) => transport().get(url, options),
    post: async (url, body, options) => transport().post(url, body, options),
    put: async (url, body, options) => transport().put(url, body, options),
    patch: async (url, body, options) => transport().patch(url, body, options),
    delete: async (url, options) => transport().delete(url, options)
  };
}
var clients = /* @__PURE__ */ new Map();
function getContextClient(config) {
  const key = `${config.context}${config.publicClient ? ":public" : ""}`;
  let client = clients.get(key);
  if (!client) {
    client = createContextClient(config);
    clients.set(key, client);
  }
  return client;
}
function resetContextClients() {
  clients.clear();
  warnedUnreachable.clear();
}
var UploadIncompleteError = class extends Error {
  /** True when the object reached S3 but the confirm leg never succeeded. */
  uploaded;
  constructor(message, uploaded, options) {
    super(message, options);
    this.name = "UploadIncompleteError";
    this.uploaded = uploaded;
  }
};
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
async function uploadToPresignedUrl(options) {
  const { ticket, file, confirm, maxConfirmAttempts = 3, signal, fetchImpl = fetch } = options;
  if (!ticket?.uploadUrl) {
    throw new ApiError({
      status: 0,
      code: "UPLOAD_TICKET_MISSING",
      message: "The upload could not be started: storage returned no upload URL. This happens when a request for one is retried under a key that was already used \u2014 ask for a new ticket rather than repeating the previous request."
    });
  }
  let response;
  try {
    response = await fetchImpl(ticket.uploadUrl, {
      method: "PUT",
      // Only the headers S3 handed back. No identity headers, no Content-Type
      // the signature did not cover.
      headers: ticket.headers ?? {},
      body: file,
      credentials: "omit",
      ...signal ? { signal } : {}
    });
  } catch (cause) {
    throw ApiError.network("Upload to storage failed", cause);
  }
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: "UPLOAD_FAILED",
      message: `Storage rejected the upload (${response.status}).`
    });
  }
  let lastError;
  for (let attempt = 0; attempt < maxConfirmAttempts; attempt += 1) {
    try {
      const confirmation = await confirm();
      return { confirmed: true, confirmation };
    } catch (error) {
      lastError = error;
      if (attempt < maxConfirmAttempts - 1) {
        await sleep(computeBackoffDelay(attempt));
      }
    }
  }
  throw new UploadIncompleteError(
    "The file reached storage but could not be confirmed. Retry the confirmation before assuming the attachment is lost.",
    true,
    { cause: lastError }
  );
}

// src/errors/friendlyMessage.ts
function asErrorLike(error) {
  if (typeof error === "object" && error !== null) return error;
  return {};
}
var IDENTIFIER_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\b[0-9a-f]{16,}\b|\b\d{10,}\b/i;
var CODE_TOKEN_RE = /\b\w+_\w+\b/;
var OPERATOR_PHRASING_RE = /\bwith ID\b|\bwith id\b|Exception\b|\bnull\b|java\.|com\.thequickbee|\bDTO\b|\bSQL\b|\bJSON\b|\bprincipal\b/i;
var DECISION_CITATION_RE = /§|\((?:ADR|INV|WP|G|B|D)-?\d+(?![A-Za-z])/;
function isCustomerSafeMessage(message) {
  if (!message || !message.trim()) return false;
  return !IDENTIFIER_RE.test(message) && !CODE_TOKEN_RE.test(message) && !OPERATOR_PHRASING_RE.test(message) && !DECISION_CITATION_RE.test(message);
}
function capitalise(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function categoryFromStatus(status) {
  switch (status) {
    case 400:
      return "validation";
    case 401:
      return "auth";
    case 403:
      return "authorization";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 422:
      return "business";
    case 429:
      return "rate_limit";
    default:
      return status !== void 0 && status >= 500 ? "server" : "unknown";
  }
}
function defaultCopy(category, subject, fallback) {
  switch (category) {
    case "validation":
      return "Some of the details entered don\u2019t look right. Please check them and try again.";
    case "auth":
      return "Your session has ended. Please sign in again.";
    case "authorization":
      return subject ? `You don\u2019t have permission to view ${subject}.` : "You don\u2019t have permission to do this.";
    case "not_found":
      return subject ? `${capitalise(subject)} isn\u2019t available right now.` : "We couldn\u2019t find what you were looking for.";
    case "conflict":
      return "This was updated by someone else while you were working. Please refresh and try again.";
    case "business":
      return fallback ?? "This can\u2019t be completed right now. Please try again, or contact support if it keeps happening.";
    case "rate_limit":
      return "Too many attempts. Please wait a moment and try again.";
    case "server":
    case "unknown":
    default:
      return fallback ?? "Something went wrong on our side. Please try again in a moment.";
  }
}
var KEEPS_SERVER_TEXT = /* @__PURE__ */ new Set(["business", "validation", "conflict"]);
function friendlyErrorMessage(error, options = {}) {
  const { status, code, message } = asErrorLike(error);
  const { subject, overrides, fallback } = options;
  if (code && overrides?.[code]) return overrides[code];
  if (status === 0 || code === "NETWORK_ERROR") {
    return "We couldn\u2019t connect. Please check your internet connection and try again.";
  }
  const mapped = code ? getErrorCodeMeta(code).category : "unknown";
  const category = mapped === "unknown" ? categoryFromStatus(status) : mapped;
  if (KEEPS_SERVER_TEXT.has(category) && isCustomerSafeMessage(message)) {
    return message;
  }
  return defaultCopy(category, subject, fallback);
}

// src/tenant/platformTenant.ts
var PLATFORM_TENANT_ID = "00000000-0000-0000-0000-000000000001";
function isPlatformTenant(tenantId) {
  return typeof tenantId === "string" && tenantId.trim().toLowerCase() === PLATFORM_TENANT_ID;
}

// src/money/decimal.ts
function toDecimalString(value) {
  if (value === null || value === void 0) return null;
  if (typeof value === "string") return value.trim() === "" ? null : value;
  if (!Number.isFinite(value)) return null;
  return String(value);
}
function isUnpriced(value) {
  return value === null || value === void 0 || typeof value === "string" && value.trim() === "";
}
function compareDecimals(a, b) {
  const left = normaliseForCompare(toDecimalString(a) ?? "0");
  const right = normaliseForCompare(toDecimalString(b) ?? "0");
  if (isZero(left) && isZero(right)) return 0;
  if (left.negative !== right.negative) return left.negative ? -1 : 1;
  const magnitude = compareMagnitude(left, right);
  return left.negative ? -magnitude : magnitude;
}
function normaliseForCompare(value) {
  const trimmed = value.trim();
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed.replace(/^\+/, "");
  const [whole = "0", fraction = ""] = unsigned.split(".");
  return { negative, whole: whole.replace(/^0+(?=\d)/, ""), fraction: fraction.replace(/0+$/, "") };
}
function isZero(value) {
  return /^0*$/.test(value.whole) && value.fraction === "";
}
function compareMagnitude(a, b) {
  if (a.whole.length !== b.whole.length) return a.whole.length < b.whole.length ? -1 : 1;
  if (a.whole !== b.whole) return a.whole < b.whole ? -1 : 1;
  const length = Math.max(a.fraction.length, b.fraction.length);
  const left = a.fraction.padEnd(length, "0");
  const right = b.fraction.padEnd(length, "0");
  if (left === right) return 0;
  return left < right ? -1 : 1;
}
function isPositive(value) {
  if (isUnpriced(value)) return false;
  return compareDecimals(value, "0") > 0;
}

export { ApiError, BRANCH_HEADER, IDEMPOTENCY_HEADER, PLATFORM_TENANT_ID, UploadIncompleteError, compareDecimals, createApiClient, createContextClient, errorCodeMap, friendlyErrorMessage, getContextClient, getErrorCodeMeta, isCustomerSafeMessage, isErrorEnvelope, isPagedEnvelope, isPagedResult, isPlatformTenant, isPositive, isSuccessEnvelope, isUnpriced, normalisePagination, parseContentDispositionFileName, parseRetryAfter, readResponseMeta, resetContextClients, saveFileDownload, toDecimalString, toFileDownload, unwrapEnvelope, uploadToPresignedUrl };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map